/// Open-conversation state from the proctoring room metadata (written by the API):
/// `{ openAudio: boolean, openAudioGroups: { "staff-<id>": ["cand-<id>", ...] } }`.
/// `openAudio` opens the whole room; a group opens one proctor's own candidates only.
export interface OpenAudioState {
  all: boolean;
  groups: Record<string, string[]>;
}

export const CLOSED: OpenAudioState = { all: false, groups: {} };

export const readOpenAudioState = (metadata?: string): OpenAudioState => {
  if (!metadata) return CLOSED;
  try {
    const parsed = JSON.parse(metadata);
    const groups: Record<string, string[]> = {};
    const raw = parsed?.openAudioGroups;
    if (raw && typeof raw === "object") {
      Object.entries(raw).forEach(([staff, members]) => {
        if (Array.isArray(members)) groups[staff] = members.filter((m): m is string => typeof m === "string");
      });
    }
    return { all: parsed?.openAudio === true, groups };
  } catch {
    return CLOSED;
  }
};

/// Candidates whose microphone the wall should play while a conversation is open.
export const audibleCandidates = (state: OpenAudioState, visibleIdentities: string[]): string[] => {
  if (state.all) return visibleIdentities;
  const members = new Set(Object.values(state.groups).flat());
  return visibleIdentities.filter((i) => members.has(i));
};

/// Whether the conversation the current proctor controls (whole room or own group) is open.
export const isMyConversationOpen = (state: OpenAudioState, staffIdentity: string, wholeRoom: boolean) =>
  wholeRoom ? state.all : !!state.groups[staffIdentity];
